import React from "react";
import type { Service } from "../../../types";
import { DataList, ListColumn, ListAction } from "../../shared";
import { Icons } from "../../shared/Icons";

type EffortAccent = "emerald" | "amber" | "red";

const EFFORT_ACCENTS: Record<
  EffortAccent,
  { bar: string; label: string; amount: string }
> = {
  emerald: {
    bar: "border-l-emerald-500",
    label: "text-emerald-800/85 dark:text-emerald-300",
    amount: "text-emerald-950 dark:text-emerald-200",
  },
  amber: {
    bar: "border-l-amber-400",
    label: "text-amber-900/85 dark:text-amber-300",
    amount: "text-amber-950 dark:text-amber-200",
  },
  red: {
    bar: "border-l-red-500",
    label: "text-red-900/85 dark:text-red-300",
    amount: "text-red-950 dark:text-red-200",
  },
};

function formatEffortAmount(
  value: number | null | undefined,
  formatCurrency: (price: number | string) => string
): string | null {
  if (value == null) return null;
  const n = typeof value === "number" ? value : parseFloat(String(value));
  if (Number.isNaN(n)) return null;
  return formatCurrency(n);
}

/** Car companies: typographic base + slim effort tiles (no outer card). */
function CarServicePrices({
  service,
  formatCurrency,
}: {
  service: Service;
  formatCurrency: (price: number | string) => string;
}) {
  const light = formatEffortAmount(service.priceLight, formatCurrency);
  const medium = formatEffortAmount(service.priceMedium, formatCurrency);
  const heavy = formatEffortAmount(service.priceHeavy, formatCurrency);
  const hasEffortTiers = light != null || medium != null || heavy != null;

  const tiers: { label: string; value: string | null; accent: EffortAccent }[] = [
    { label: "Leve", value: light, accent: "emerald" },
    { label: "Médio", value: medium, accent: "amber" },
    { label: "Pesado", value: heavy, accent: "red" },
  ];

  if (!hasEffortTiers) {
    return (
      <div className="w-full">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
          Preço base
        </p>
        <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-emerald-600 dark:text-emerald-400 sm:text-[1.375rem] md:text-2xl">
          {formatCurrency(service.price)}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 md:min-w-[28rem]">
      <div className="flex flex-col gap-5 md:flex-row md:items-stretch md:gap-0 md:divide-x md:divide-slate-200 dark:md:divide-slate-600">
        <div className="shrink-0 md:min-w-[7rem] md:pr-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Preço base
          </p>
          <p className="mt-1 text-xl font-bold tabular-nums tracking-tight text-emerald-600 dark:text-emerald-400 md:text-lg lg:text-xl">
            {formatCurrency(service.price)}
          </p>
        </div>

        <div className="min-w-0 flex-1 md:min-w-[20rem] md:pl-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Por esforço do veículo
          </p>
          <div
            className="mt-2 grid gap-2 sm:gap-3"
            style={{ gridTemplateColumns: "repeat(3, minmax(7.25rem, 1fr))" }}
          >
            {tiers.map(({ label, value, accent }) => {
              const a = EFFORT_ACCENTS[accent];
              return (
                <div
                  key={label}
                  className={`rounded-xl border border-slate-200/80 dark:border-slate-600 bg-slate-50/40 dark:bg-slate-900/50 px-2 py-2.5 shadow-sm backdrop-blur-[2px] sm:px-2.5 sm:py-3 border-l-[3px] ${a.bar}`}
                >
                  <p className={`text-[10px] font-bold uppercase tracking-wide ${a.label}`}>
                    {label}
                  </p>
                  <p
                    className={`mt-1.5 whitespace-nowrap text-sm font-bold tabular-nums leading-none sm:text-base ${a.amount}`}
                  >
                    {value ?? "—"}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ServicesListViewProps {
  services: Service[];
  onEdit: (service: Service) => void;
  onDelete: (serviceId: number) => void;
  onManageSubServices: (service: Service) => void;
  loading?: boolean;
  companyCategory?: "car" | "barbershop";
}

export const ServicesListView: React.FC<ServicesListViewProps> = ({
  services,
  onEdit,
  onDelete,
  onManageSubServices,
  loading = false,
  companyCategory,
}) => {
  const formatCurrency = (price: number | string) => {
    const numericPrice = typeof price === "string" ? parseFloat(price) : price;
    return `R$ ${(isNaN(numericPrice) ? 0 : numericPrice).toFixed(2)}`;
  };

  const formatDuration = (minutes: number | string) => {
    const numericMinutes =
      typeof minutes === "string" ? parseInt(minutes) : minutes;
    if (numericMinutes >= 60) {
      const hours = Math.floor(numericMinutes / 60);
      const remainingMinutes = numericMinutes % 60;
      return remainingMinutes > 0
        ? `${hours}h ${remainingMinutes}min`
        : `${hours}h`;
    }
    return `${numericMinutes}min`;
  };

  const isCar = companyCategory === "car";

  const columns: ListColumn<Service>[] = [
    {
      key: "name",
      label: "Serviço",
      width: isCar ? "minmax(9rem, 2fr)" : "3fr",
      render: (service) => (
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{service.name}</p>
          {companyCategory === "barbershop" && service.professional?.name && (
            <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">
              👨‍💼 {service.professional.name}
            </p>
          )}
          {service.description && (
            <p className="text-xs text-gray-500 dark:text-slate-400 truncate max-w-xs">
              {service.description}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "price",
      label: companyCategory === "car" ? "Preços" : "Preço",
      width: isCar ? "minmax(26rem, 3.5fr)" : "1fr",
      className: companyCategory === "car" ? "items-start" : undefined,
      render: (service) => {
        if (companyCategory === "car") {
          return <CarServicePrices service={service} formatCurrency={formatCurrency} />;
        }

        return (
          <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            {formatCurrency(service.price)}
          </span>
        );
      },
    },
    ...(companyCategory !== "car"
      ? [
          {
            key: "duration",
            label: "Duração",
            width: "1fr",
            render: (service: Service) => (
              <span className="text-sm text-gray-900 dark:text-slate-100">
                {service.duration ? formatDuration(service.duration) : "N/A"}
              </span>
            ),
          },
        ]
      : []),
    {
      key: "expirationDays",
      label: "Validade",
      width: "0.75fr",
      render: (service) => (
        <span className="text-sm text-gray-900 dark:text-slate-100">
          {service.expirationDays != null
            ? `${service.expirationDays} dias`
            : "—"}
        </span>
      ),
    },
  ];

  const actions: ListAction<Service>[] = [
    {
      label: "Gerenciar sub-serviços",
      icon: <Icons.Build sx={{ fontSize: 18 }} />,
      onClick: (service) => onManageSubServices(service),
      className:
        "p-1.5 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/40 rounded-lg transition-all duration-200",
      title: "Gerenciar sub-serviços",
      disabled: () => loading,
    },
    {
      label: "Editar serviço",
      icon: <Icons.Edit sx={{ fontSize: 18 }} />,
      onClick: (service) => onEdit(service),
      className:
        "p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg transition-all duration-200",
      title: "Editar serviço",
      disabled: () => loading,
    },
    {
      label: "Excluir serviço",
      icon: <Icons.Delete sx={{ fontSize: 18 }} />,
      onClick: (service) => onDelete(service.id),
      className:
        "p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-all duration-200",
      title: "Excluir serviço",
      disabled: () => loading,
    },
  ];

  return (
    <DataList<Service>
      data={services}
      columns={columns}
      actions={actions}
      keyExtractor={(service) => service.id}
      emptyMessage="Nenhum serviço encontrado"
      loading={loading}
      onRowClick={(service) => onEdit(service)}
      itemLabel={services.length === 1 ? "serviço" : "serviços"}
      desktopMinWidth={isCar ? "65rem" : "49rem"}
    />
  );
};
